import { useEffect, useLayoutEffect, useRef } from 'react';

import type { CheckoutContextType, OpenCheckoutOptions, StepKey } from '../Checkout.types';

import {
  clearDroneOrder,
  clearSubscriptionOrder,
  getDroneOrder,
  getSubscriptionOrder,
  hasPersistedOrderPrices,
  restorePersistedOrder
} from './orderPersistence';

type CheckoutPersistence = Pick<
  CheckoutContextType,
  | 'currentStep'
  | 'isCheckoutOpen'
  | 'orderPrices'
  | 'setSelectedProducts'
  | 'setOrderPrices'
  | 'setPaymentMethod'
  | 'setCouponDiscount'
  | 'setExtraData'
>;

type UseKlarnaOrderPersistenceOptions = {
  checkout: CheckoutPersistence;
  stepKeys: StepKey[];
  openOptions?: OpenCheckoutOptions | null;
  variant: 'drone' | 'subscription';
};

export function useKlarnaOrderPersistence({
  checkout,
  stepKeys,
  openOptions,
  variant
}: UseKlarnaOrderPersistenceOptions): void {
  const thankYouStepIndex = stepKeys.length;
  const getOrder = variant === 'drone' ? getDroneOrder : getSubscriptionOrder;
  const clearOrder = variant === 'drone' ? clearDroneOrder : clearSubscriptionOrder;
  const restoredRef = useRef(false);

  useLayoutEffect(() => {
    if (!checkout.isCheckoutOpen) {
      return;
    }

    const onThankYouStep =
      openOptions?.step === thankYouStepIndex || stepKeys[checkout.currentStep - 1] === 'thankyou';

    if (!onThankYouStep) {
      return;
    }

    const order = getOrder();
    if (!order || !hasPersistedOrderPrices(order.orderPrices)) {
      return;
    }

    const contextHasPrices = hasPersistedOrderPrices(checkout.orderPrices);
    if (restoredRef.current && contextHasPrices) {
      return;
    }

    restorePersistedOrder(order, checkout);
    clearOrder();
    restoredRef.current = true;
  }, [
    checkout.isCheckoutOpen,
    checkout.currentStep,
    checkout.orderPrices,
    openOptions?.step,
    thankYouStepIndex,
    stepKeys,
    variant,
    checkout.setSelectedProducts,
    checkout.setOrderPrices,
    checkout.setPaymentMethod,
    checkout.setCouponDiscount,
    checkout.setExtraData
  ]);

  const wasCheckoutOpenRef = useRef(false);
  useEffect(() => {
    if (wasCheckoutOpenRef.current && !checkout.isCheckoutOpen) {
      clearOrder();
      restoredRef.current = false;
    }
    wasCheckoutOpenRef.current = checkout.isCheckoutOpen;
  }, [checkout.isCheckoutOpen, variant]);
}
